import { useState, useEffect, useMemo, useRef } from 'react';
import DemoLabLanding from '../components/workspace/DemoLabLanding';
import QuickStart from '../components/onboarding/QuickStart';
import { getApiUrl } from '../utils/api';
import { useUserInstances } from '../contexts/UserInstancesContext';

/** Don't refetch strategies on focus more often than this. */
const STRATEGIES_REFETCH_GAP_MS = 30_000;

/**
 * Dashboard Page - Main landing page for the platform.
 *
 * Branches on activity signal:
 * - New user (no running instances, no strategies) → QuickStart above,
 *   DemoLabLanding below as "Explore demo data."
 * - Returning user → DemoLabLanding only (unchanged).
 *
 * Fail-safe: if activity-signal fetches error, default to returning-user
 * view so we never misclassify a returning user as new.
 *
 * Data sources:
 * - Instances come from the shared UserInstancesContext (single poller —
 *   each /api/compute/instances/user call triggers a backend<->AWS sync, so
 *   this page must not run its own interval).
 * - Strategies are fetched once on mount + on window focus (throttled). The
 *   list can't change server-side while the user stares at the dashboard, so
 *   an interval would be pure waste.
 *
 * Route: /dashboard
 */
export default function LabPage() {
  const [username, setUsername] = useState<string>('there');
  const { instances, hasLoaded: instancesLoaded } = useUserInstances();
  // null = not yet settled; used to gate the new-user branch.
  const [strategiesCount, setStrategiesCount] = useState<number | null>(null);
  const [strategiesFailed, setStrategiesFailed] = useState(false);
  const lastStrategiesFetchRef = useRef(0);

  // Instances feed cost + session badges even for returning users.
  const { activeSessions, runningCost } = useMemo(() => {
    const running = instances.filter(
      (i) => i.status === 'running' || i.status === 'pending' || i.status === 'launching'
    );
    return {
      activeSessions: running.length,
      // API returns hourly_cost, not hourly_rate
      runningCost: running.reduce((sum, i) => sum + (i.hourly_cost || 0), 0),
    };
  }, [instances]);

  // Fetch user profile to get display name
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const response = await fetch(`${getApiUrl()}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.display_name) {
            // Extract first name
            setUsername(data.display_name.split(' ')[0]);
          }
        }
      } catch {
        // Ignore errors - will use default "there"
      }
    };

    fetchProfile();
  }, []);

  // Strategy count — fetched once on mount, refreshed on window focus.
  // No interval: nothing server-side changes this list during an idle session.
  useEffect(() => {
    const fetchStrategies = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const response = await fetch(`${getApiUrl()}/api/v1/strategies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          setStrategiesFailed(true);
          return;
        }
        const data = await response.json();
        // Endpoint shape varies across callers; support both flat array
        // and { strategies: [...] } envelopes.
        const list = Array.isArray(data) ? data : (data.strategies || []);
        lastStrategiesFetchRef.current = Date.now();
        setStrategiesCount(list.length);
        setStrategiesFailed(false);
      } catch (err) {
        console.debug('Could not fetch strategies:', err);
        setStrategiesFailed(true);
      }
    };

    fetchStrategies();

    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastStrategiesFetchRef.current < STRATEGIES_REFETCH_GAP_MS) return;
      fetchStrategies();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Activity signal — new-user flag gates the QuickStart surface. Stays null
  // until both sources settle; only an explicit `true` shows onboarding, so a
  // failed fetch (fail-safe) renders the returning-user view.
  const isNewUser: boolean | null = strategiesFailed
    ? false
    : instancesLoaded && strategiesCount !== null
      ? activeSessions === 0 && strategiesCount === 0
      : null;

  // New-user view: QuickStart full-width, demo content reframed below.
  if (isNewUser === true) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-6 pt-8">
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-white">Welcome, {username}</h1>
            <p className="mt-1 text-sm text-gray-400">
              Launch a notebook on a GPU, or browse the feed and templates for ideas.
            </p>
          </div>
          <div className="mt-6">
            <QuickStart compact />
          </div>
          <div className="mt-10 mb-2 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-800" />
            <span className="text-xs uppercase tracking-wider text-gray-500">Explore demo data</span>
            <div className="h-px flex-1 bg-gray-800" />
          </div>
        </div>
        <DemoLabLanding
          username={username}
          activeSessions={activeSessions}
          runningCost={runningCost}
          embedded
        />
      </div>
    );
  }

  // Returning user (or fail-safe while fetches resolve): existing behavior.
  return (
    <DemoLabLanding
      username={username}
      activeSessions={activeSessions}
      runningCost={runningCost}
    />
  );
}
